import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSettings } from '../contexts/SettingsContext';

const T = {
  green800:  '#1a4a1c',
  green700:  '#1e5e20',
  green600:  '#2a7a2d',
  green500:  '#3a9a3e',
  green200:  '#b8e0bb',
  green100:  '#dff0e0',
  green50:   '#f0f9f1',
  bg:        '#f5f8f5',
  white:     '#ffffff',
  text:      '#1a2e1b',
  textMed:   '#4a6a4c',
  textLight: '#8aaa8c',
  border:    '#e0ece0',
  shadow:    'rgba(30,80,32,0.10)',
  amber:     '#c8761a',
};

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

function monthKeyToLabel(key) {
  const [year, mon] = key.split('-');
  return `${MONTH_NAMES[parseInt(mon, 10) - 1]} ${year}`;
}

// ── Mobile Notes Card ──────────────────────────────────────────────────────────
function MobileNotesCard({ monthName, activeYear, monthKey, text, setText, persist, monthlyNotes }) {
  const [editing, setEditing]       = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [openMonth, setOpenMonth]   = useState(null);
  const [saved, setSaved]           = useState(false);
  const saveTimer  = useRef(null);
  const savedTimer = useRef(null);

  const preview    = text.trim() ? text.trim().split('\n')[0] : null;
  const extraLines = text.trim().split('\n').length - 1;

  const handleChange = (e) => {
    const value = e.target.value;
    setText(value);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      persist(value);
      setSaved(true);
      clearTimeout(savedTimer.current);
      savedTimer.current = setTimeout(() => setSaved(false), 1500);
    }, 900);
  };

  useEffect(() => () => {
    clearTimeout(saveTimer.current);
    clearTimeout(savedTimer.current);
  }, []);

  // Past notes — all months with content except current, sorted descending
  const pastNotes = Object.entries(monthlyNotes)
    .filter(([key, val]) => key !== monthKey && val?.content?.trim())
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([key, val]) => ({ label: monthKeyToLabel(key), text: val.content }));

  return (
    <>
      <div style={{
        background: T.white, borderRadius: 20, padding: 16,
        boxShadow: `0 2px 12px ${T.shadow}`, marginTop: 16,
      }}>
        {/* Header row */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: editing ? 12 : (preview ? 10 : 8),
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 16 }}>📓</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{monthName} Notes</span>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {saved && <span style={{ fontSize: 10, color: T.green700, fontWeight: 600 }}>Saved ✓</span>}
            {editing
              ? <button
                  onClick={() => setEditing(false)}
                  style={{ background: T.green700, color: 'white', border: 'none', borderRadius: 8, padding: '4px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                >Done</button>
              : <button
                  onClick={() => setEditing(true)}
                  style={{ background: T.green50, color: T.green700, border: `1px solid ${T.green200}`, borderRadius: 8, padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                >{text ? 'Edit' : '+ Add'}</button>
            }
          </div>
        </div>

        {/* Collapsed preview */}
        {!editing && (
          <div onClick={() => setEditing(true)} style={{ cursor: 'pointer' }}>
            {preview
              ? <div style={{ fontSize: 12, color: T.textMed, lineHeight: 1.5, borderLeft: `3px solid ${T.green200}`, paddingLeft: 10 }}>
                  {preview}
                  {extraLines > 0 && (
                    <span style={{ color: T.textLight }}>
                      {' '}…{extraLines} more line{extraLines > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              : <div style={{ fontSize: 12, color: T.textLight, fontStyle: 'italic' }}>
                  Tap to log your {monthName} observations…
                </div>
            }
          </div>
        )}

        {/* Editor */}
        {editing && (
          <>
            <textarea
              autoFocus
              value={text}
              onChange={handleChange}
              maxLength={10000}
              placeholder={`${monthName.slice(0, 3)} 12 – First mow of the season\n${monthName.slice(0, 3)} 19 – Noticed thin patches near fence`}
              style={{
                width: '100%', boxSizing: 'border-box', minHeight: 100,
                border: `1.5px solid ${T.border}`, borderRadius: 12, padding: '10px 12px',
                fontSize: 12, color: T.text, lineHeight: 1.6, resize: 'vertical',
                fontFamily: "'DM Sans', sans-serif", outline: 'none', background: T.bg,
              }}
              onFocus={e => (e.target.style.borderColor = T.green500)}
              onBlur={e  => (e.target.style.borderColor = T.border)}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
              <span style={{ fontSize: 10, color: T.textLight }}>Auto-saves as you type</span>
              <span style={{ fontSize: 10, color: text.length > 9000 ? T.amber : T.textLight }}>
                {text.length.toLocaleString()} / 10,000
              </span>
            </div>
          </>
        )}

        {/* Past notes link */}
        <button
          onClick={() => setShowHistory(true)}
          style={{
            marginTop: 10, background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 12, color: T.green700, fontWeight: 600, padding: 0,
            display: 'flex', alignItems: 'center', gap: 4,
          }}
        >
          View past notes <span style={{ fontSize: 14 }}>›</span>
        </button>
      </div>

      {/* Past Notes Bottom Sheet */}
      {showHistory && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
          <div onClick={() => setShowHistory(false)} style={{ flex: 1, background: 'rgba(0,0,0,0.4)' }} />
          <div style={{
            background: T.white, borderRadius: '24px 24px 0 0', paddingBottom: 40,
            maxHeight: '70vh', display: 'flex', flexDirection: 'column',
          }}>
            {/* Drag handle */}
            <div style={{ width: 40, height: 4, borderRadius: 99, background: T.border, margin: '12px auto 16px' }} />
            {/* Sheet header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '0 20px 12px', borderBottom: `1px solid ${T.border}`,
            }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: T.text }}>Note History</span>
              <button
                onClick={() => setShowHistory(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.textLight, fontSize: 20 }}
              >✕</button>
            </div>
            {/* Sheet content */}
            <div style={{ overflowY: 'auto', padding: '12px 20px' }}>
              {/* Current month */}
              <div style={{ marginBottom: 12, background: T.green50, borderRadius: 14, overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', padding: '12px 14px', gap: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: T.green700 }}>{monthName} {activeYear}</span>
                  <span style={{
                    fontSize: 11, color: T.green700, background: T.green100,
                    borderRadius: 99, padding: '1px 8px',
                  }}>Current</span>
                </div>
                <div style={{
                  padding: '10px 14px 12px', fontSize: 12, color: T.textMed, lineHeight: 1.6,
                  borderTop: `1px solid ${T.green200}`, whiteSpace: 'pre-line',
                }}>
                  {text || <span style={{ color: T.textLight, fontStyle: 'italic' }}>No notes yet this month</span>}
                </div>
              </div>
              {/* Past months */}
              {pastNotes.length === 0 && (
                <div style={{ fontSize: 12, color: T.textLight, textAlign: 'center', padding: '16px 0', fontStyle: 'italic' }}>
                  No past notes yet
                </div>
              )}
              {pastNotes.map((n, i) => (
                <div key={i} style={{ marginBottom: 8, background: T.white, border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden' }}>
                  <div
                    onClick={() => setOpenMonth(openMonth === i ? null : i)}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', cursor: 'pointer' }}
                  >
                    <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{n.label}</span>
                    <span style={{
                      fontSize: 14, color: T.textLight,
                      display: 'inline-block', transition: 'transform 0.15s',
                      transform: openMonth === i ? 'rotate(90deg)' : 'none',
                    }}>›</span>
                  </div>
                  {openMonth === i && (
                    <div style={{
                      padding: '10px 14px 12px', fontSize: 12, color: T.textMed,
                      lineHeight: 1.6, borderTop: `1px solid ${T.border}`, whiteSpace: 'pre-line',
                    }}>
                      {n.text}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Desktop Notes Panel ────────────────────────────────────────────────────────
function DesktopNotesPanel({ monthName, monthKey, text, setText, persist, monthlyNotes }) {
  const [showHistory, setShowHistory] = useState(false);
  const [openMonth, setOpenMonth]     = useState(null);
  const [saved, setSaved]             = useState(false);
  const saveTimer  = useRef(null);
  const savedTimer = useRef(null);

  const handleChange = (e) => {
    const value = e.target.value;
    setText(value);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      persist(value);
      setSaved(true);
      clearTimeout(savedTimer.current);
      savedTimer.current = setTimeout(() => setSaved(false), 1500);
    }, 900);
  };

  useEffect(() => () => {
    clearTimeout(saveTimer.current);
    clearTimeout(savedTimer.current);
  }, []);

  const pastNotes = Object.entries(monthlyNotes)
    .filter(([key, val]) => key !== monthKey && val?.content?.trim())
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([key, val]) => ({ label: monthKeyToLabel(key), text: val.content }));

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ background: T.white, borderRadius: 16, padding: '20px 22px', boxShadow: `0 1px 4px ${T.shadow}` }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 18 }}>📓</span>
            <span style={{ fontSize: 15, fontWeight: 700, color: T.text }}>{monthName} Notes</span>
            {saved && <span style={{ fontSize: 11, color: T.green700, fontWeight: 600, marginLeft: 4 }}>✓ Saved</span>}
          </div>
          <button
            onClick={() => setShowHistory(h => !h)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: showHistory ? T.green50 : 'none',
              border: `1px solid ${showHistory ? T.green200 : T.border}`,
              borderRadius: 8, padding: '5px 12px', fontSize: 12, fontWeight: 600,
              color: showHistory ? T.green700 : T.textMed, cursor: 'pointer',
            }}
          >
            <span>Note History</span>
            <span style={{
              fontSize: 12, display: 'inline-block',
              transform: showHistory ? 'rotate(90deg)' : 'none',
              transition: 'transform 0.15s',
            }}>›</span>
          </button>
        </div>

        <div style={{ display: 'flex', gap: 16 }}>
          {/* Textarea */}
          <div style={{ flex: 1 }}>
            <textarea
              value={text}
              onChange={handleChange}
              maxLength={10000}
              placeholder={`Log your ${monthName} lawn care observations…\n\nExample:\n${monthName.slice(0, 3)} 12 – First mow of the season, fertilizer applied\n${monthName.slice(0, 3)} 19 – Noticed thin patches near fence, scheduled reseeding`}
              style={{
                width: '100%', boxSizing: 'border-box', height: 130,
                border: `1.5px solid ${T.border}`, borderRadius: 12,
                padding: '12px 14px', fontSize: 13, color: T.text, lineHeight: 1.6,
                resize: 'vertical', fontFamily: "'DM Sans', sans-serif", outline: 'none',
                background: T.bg, transition: 'border-color 0.15s',
              }}
              onFocus={e => (e.target.style.borderColor = T.green500)}
              onBlur={e  => (e.target.style.borderColor = T.border)}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
              <span style={{ fontSize: 11, color: T.textLight }}>✓ Auto-saves as you type</span>
              <span style={{ fontSize: 11, color: text.length > 9000 ? T.amber : T.textLight }}>
                {text.length.toLocaleString()} / 10,000
              </span>
            </div>
          </div>

          {/* Inline history panel */}
          {showHistory && (
            <div style={{ width: 280, flexShrink: 0 }}>
              <div style={{
                fontSize: 11, fontWeight: 700, color: T.textLight,
                textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10,
              }}>Past Notes</div>
              {pastNotes.length === 0 && (
                <div style={{ fontSize: 12, color: T.textLight, fontStyle: 'italic', padding: '8px 0' }}>
                  No past notes yet
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 180, overflowY: 'auto' }}>
                {pastNotes.map((n, i) => (
                  <div key={i} style={{ border: `1px solid ${T.border}`, borderRadius: 12, overflow: 'hidden' }}>
                    <div
                      onClick={() => setOpenMonth(openMonth === i ? null : i)}
                      style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '9px 12px', cursor: 'pointer',
                        background: openMonth === i ? T.green50 : T.white,
                      }}
                    >
                      <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{n.label}</span>
                      <span style={{
                        fontSize: 13, color: T.textLight, display: 'inline-block',
                        transform: openMonth === i ? 'rotate(90deg)' : 'none',
                        transition: 'transform 0.15s',
                      }}>›</span>
                    </div>
                    {openMonth === i && (
                      <div style={{
                        padding: '8px 12px 10px', fontSize: 11, color: T.textMed,
                        lineHeight: 1.6, borderTop: `1px solid ${T.border}`,
                        whiteSpace: 'pre-line', background: T.bg,
                      }}>
                        {n.text}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Orchestrator ───────────────────────────────────────────────────────────────
const NotesSection = ({ monthName, activeYear, isDesktop = false }) => {
  const { monthlyNotes, setMonthlyNotes } = useSettings();
  const [text, setText] = useState('');

  const monthKey = `${activeYear}-${String(
    MONTH_NAMES.indexOf(monthName) + 1
  ).padStart(2, '0')}`;

  useEffect(() => {
    setText(monthlyNotes[monthKey]?.content || '');
  }, [monthKey, monthlyNotes]);

  const persist = useCallback((value) => {
    setMonthlyNotes({
      ...monthlyNotes,
      [monthKey]: { content: value, lastUpdated: new Date().toISOString() },
    });
  }, [monthKey, monthlyNotes, setMonthlyNotes]);

  const shared = { monthName, activeYear, monthKey, text, setText, persist, monthlyNotes };

  if (isDesktop) return <DesktopNotesPanel {...shared} />;
  return <MobileNotesCard {...shared} />;
};

export default NotesSection;
